import { useCallback, useEffect, useRef, useState } from "react";
import { LIVE_INTERVAL_MS, useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, Tooltip,
  FormControlLabel, Switch, Paper, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fmtArDateTime } from "../services/monitoring";
import {
  getCobros, fmtMoney, fmtBd, fmtOpTime, fmtOpDateTime, isRecent,
  type Cobros, type CobrosAcquirer,
} from "../services/monitoreoVivo";

const RECENT_MIN = 5;             // fila verde si el collector cobró hace < 5 min
const FEED_LIMIT = 50;

const nf = new Intl.NumberFormat("es-AR");

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 170, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

function AcquirerTable({ acq }: { acq: CobrosAcquirer }) {
  const isBind = acq.key === "AR#BIND";
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{acq.key}</Typography>
        <Chip size="small" label={`Fecha de negocio ${fmtBd(acq.business_date)}`} variant="outlined" />
        <Typography variant="body2" color="text.secondary">
          {nf.format(acq.totals.count)} cobros · {fmtMoney(acq.totals.total, acq.currency)}
        </Typography>
      </Stack>
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Collector</TableCell>
              <TableCell align="right">Cobros</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Prom</TableCell>
              <TableCell align="right">Min</TableCell>
              <TableCell align="right">Max</TableCell>
              <TableCell>Primero</TableCell>
              <TableCell>Último</TableCell>
              {isBind && <TableCell align="right">Neto est.</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {acq.collectors.map((r) => {
              const active = isRecent(r.last, RECENT_MIN);
              // 🚩 del informe: los costos fijos superan el 0.25% del volumen
              const flag = isBind && (r.count * 20 + 250) > r.total * 0.0025;
              return (
                <TableRow key={r.collector} sx={active ? { bgcolor: "#F0FDF4" } : undefined}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.collector} {flag ? "🚩" : ""}
                    </Typography>
                    {r.sub_processor && (
                      <Typography variant="caption" color="text.secondary">
                        {r.sub_processor}{r.account ? ` (${r.account})` : ""}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{nf.format(r.count)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.total, acq.currency)}</TableCell>
                  <TableCell align="right">{fmtMoney(r.avg, acq.currency)}</TableCell>
                  <TableCell align="right">{fmtMoney(r.min, acq.currency)}</TableCell>
                  <TableCell align="right">{fmtMoney(r.max, acq.currency)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtOpTime(r.first)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {fmtOpTime(r.last)}
                    {active && <Chip size="small" label="activo" color="success" variant="outlined" sx={{ ml: 0.5, height: 18 }} />}
                  </TableCell>
                  {isBind && (
                    <TableCell align="right" sx={{ color: (r.net ?? 0) <= 0 ? "#B91C1C" : undefined, fontWeight: 600 }}>
                      {r.net != null ? fmtMoney(r.net, acq.currency) : "—"}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {acq.collectors.length === 0 && (
              <TableRow><TableCell colSpan={isBind ? 9 : 8}>
                <Typography variant="body2" color="text.secondary">Sin cobros todavía.</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function MonitoreoCobros() {
  const [data, setData] = useState<Cobros | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [tab, setTab] = useState<string>("feed");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  // payment_ids ya vistos → los que no están son "nuevos" en este refresh.
  const seenIds = useRef<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await getCobros({ feed_limit: FEED_LIMIT });
      const fresh = new Set<string>();
      if (seenIds.current.size > 0) {
        for (const f of d.feed) if (!seenIds.current.has(f.payment_id)) fresh.add(f.payment_id);
      }
      for (const f of d.feed) seenIds.current.add(f.payment_id);
      setNewIds(fresh);
      setData(d);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, LIVE_INTERVAL_MS, auto);

  const totalCount = data?.acquirers.reduce((s, a) => s + a.totals.count, 0) ?? 0;

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <Typography variant="h5">Cobros del día</Typography>
          <Typography variant="body2" color="text.secondary">
            Cobros recibidos del día de negocio, por acquirer y collector. Misma fuente que el informe parcial de cobros. Horarios en hora Argentina.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={`Auto-refresh (${LIVE_INTERVAL_MS / 1000}s)`}
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {fmtArDateTime(updatedAt)}
          </Typography>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <StatTile label="Cobros hoy" value={nf.format(totalCount)} />
          {data?.acquirers.map((a) => (
            <StatTile
              key={a.key}
              label={a.key}
              value={fmtMoney(a.totals.total, a.currency)}
              sub={`${nf.format(a.totals.count)} cobros`}
            />
          ))}
        </Stack>

        {data && (
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="feed" label="Últimos cobros" />
            {data.acquirers.map((a) => (
              <Tab key={a.key} value={a.key} label={`${a.key} (${nf.format(a.totals.count)})`} />
            ))}
          </Tabs>
        )}

        {data && tab !== "feed" && (() => {
          const acq = data.acquirers.find((a) => a.key === tab);
          return acq ? <AcquirerTable acq={acq} /> : null;
        })()}

        {data && tab === "feed" && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Hora</TableCell>
                    <TableCell>Acquirer</TableCell>
                    <TableCell>Collector</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Referencia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.feed.map((f) => (
                    <TableRow
                      key={f.payment_id}
                      sx={newIds.has(f.payment_id) ? { bgcolor: "#F0FDF4" } : undefined}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {fmtOpDateTime(f.ts)}
                        {newIds.has(f.payment_id) && (
                          <Chip size="small" label="nuevo" color="success" sx={{ ml: 0.5, height: 18 }} />
                        )}
                      </TableCell>
                      <TableCell>{f.acquirer}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{f.collector}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(f.amount, f.currency)}</TableCell>
                      <TableCell>{f.from}</TableCell>
                      <TableCell>{f.reference}</TableCell>
                    </TableRow>
                  ))}
                  {data.feed.length === 0 && (
                    <TableRow><TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">Sin cobros todavía.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
